// src/edu-world/systems/mission-direction.ts
// Active Mission Direction Icon — persistent screen-edge indicator toward mission target
// Phaser-dependent module (Phase 3) — takes scene reference
// Requirements: 25.5

import Phaser from 'phaser';

// ─── Types ───

export interface MissionDirectionConfig {
  /** Icon asset key to display (friendly character/object) */
  iconKey: string;
  /** Fallback emoji if icon asset not loaded */
  fallbackEmoji: string;
  /** Margin from screen edge in pixels */
  edgeMargin: number;
  /** Icon scale */
  iconScale: number;
  /** Minimum distance from player to target before showing (pixels) */
  minDistanceToShow: number;
}

// ─── Default Configuration ───

const DEFAULT_CONFIG: MissionDirectionConfig = {
  iconKey: 'mission-direction-icon',
  fallbackEmoji: '⭐',
  edgeMargin: 40,
  iconScale: 0.8,
  minDistanceToShow: 250,
};

// ─── MissionDirectionIcon ───

/**
 * Shows a persistent direction icon near the screen edge pointing toward
 * the active mission target. Uses a friendly character/object icon.
 *
 * Key rules:
 * - Only visible while a Primary_Mission is active
 * - Points toward the mission target position
 * - Positioned near the screen edge in the direction of the target
 * - Does NOT show if player is currently moving (reduces visual clutter)
 * - Friendly, non-stressful visual (no urgency)
 */
export class MissionDirectionIcon {
  private scene: Phaser.Scene;
  private config: MissionDirectionConfig;

  private iconContainer: Phaser.GameObjects.Container | null = null;
  private targetPosition: { x: number; y: number } | null = null;
  private isVisible: boolean = false;
  private isPlayerMoving: boolean = false;

  constructor(scene: Phaser.Scene, config?: Partial<MissionDirectionConfig>) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Show the direction icon pointing toward a target position.
   * Call this when a Primary_Mission becomes active.
   *
   * @param targetPos - World position of the mission target (e.g., NPC location)
   */
  show(targetPos: { x: number; y: number }): void {
    this.targetPosition = targetPos;
    this.isVisible = true;

    if (!this.iconContainer) {
      this.createIcon();
    }

    this.updateIconVisibility();
  }

  /**
   * Hide the direction icon.
   * Call this when the Primary_Mission is completed or cancelled.
   */
  hide(): void {
    this.isVisible = false;
    this.targetPosition = null;

    if (this.iconContainer) {
      this.scene.tweens.add({
        targets: this.iconContainer,
        alpha: 0,
        scale: 0.5,
        duration: 300,
        ease: 'Sine.easeIn',
        onComplete: () => {
          this.destroyIcon();
        },
      });
    }
  }

  /**
   * Update the icon position and visibility based on player state.
   * Should be called every frame in the scene update loop.
   *
   * @param playerPos - Current player world position
   * @param isMoving - Whether the player is currently moving
   */
  update(playerPos: { x: number; y: number }, isMoving: boolean): void {
    this.isPlayerMoving = isMoving;

    if (!this.isVisible || !this.targetPosition) {
      return;
    }

    this.updateIconVisibility();

    if (!this.iconContainer || !this.iconContainer.visible) {
      return;
    }

    // Calculate direction from player to target
    const dx = this.targetPosition.x - playerPos.x;
    const dy = this.targetPosition.y - playerPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Don't show if player is very close to target
    if (distance < this.config.minDistanceToShow) {
      this.iconContainer.setVisible(false);
      return;
    }

    // Get camera/viewport bounds
    const camera = this.scene.cameras.main;
    const viewWidth = camera.width;
    const viewHeight = camera.height;

    // Calculate normalized direction
    const normX = dx / distance;
    const normY = dy / distance;

    // Position icon at the edge of the viewport in the direction of the target
    const centerX = viewWidth / 2;
    const centerY = viewHeight / 2;
    const margin = this.config.edgeMargin;

    // Calculate where the direction line intersects the viewport edge
    const halfW = centerX - margin;
    const halfH = centerY - margin;

    let edgeX: number;
    let edgeY: number;

    // Determine which edge the icon should sit on
    const angleToTarget = Math.atan2(normY, normX);
    const cornerAngle = Math.atan2(halfH, halfW);

    if (Math.abs(angleToTarget) < cornerAngle) {
      // Right edge
      edgeX = centerX + halfW;
      edgeY = centerY + normY * (halfW / normX);
    } else if (Math.abs(angleToTarget) > Math.PI - cornerAngle) {
      // Left edge
      edgeX = centerX - halfW;
      edgeY = centerY - normY * (halfW / normX);
    } else if (angleToTarget > 0) {
      // Bottom edge
      edgeX = centerX + normX * (halfH / normY);
      edgeY = centerY + halfH;
    } else {
      // Top edge
      edgeX = centerX - normX * (halfH / normY);
      edgeY = centerY - halfH;
    }

    // Clamp to viewport bounds
    edgeX = Phaser.Math.Clamp(edgeX, margin, viewWidth - margin);
    edgeY = Phaser.Math.Clamp(edgeY, margin, viewHeight - margin);

    // Position the icon (using screen-space coordinates via scrollFactor 0)
    this.iconContainer.setPosition(edgeX, edgeY);

    // Rotate icon to point toward target
    const rotation = Math.atan2(dy, dx);
    this.iconContainer.setRotation(rotation);
  }

  /**
   * Get whether the icon is currently visible on screen.
   */
  getIsVisible(): boolean {
    return this.isVisible && !this.isPlayerMoving;
  }

  /**
   * Clean up all resources.
   */
  destroy(): void {
    this.destroyIcon();
    this.isVisible = false;
    this.targetPosition = null;
  }

  // ─── Private ───

  private createIcon(): void {
    this.iconContainer = this.scene.add.container(0, 0);
    this.iconContainer.setDepth(50); // Above most world elements
    this.iconContainer.setScrollFactor(0); // Fixed to camera/screen

    // Try to use image asset, fall back to emoji text
    let iconElement: Phaser.GameObjects.Image | Phaser.GameObjects.Text;

    if (this.scene.textures.exists(this.config.iconKey)) {
      iconElement = this.scene.add.image(0, 0, this.config.iconKey);
      iconElement.setScale(this.config.iconScale);
    } else {
      iconElement = this.scene.add.text(0, 0, this.config.fallbackEmoji, {
        fontSize: '28px',
      }).setOrigin(0.5);
    }

    // Soft glow background
    const glow = this.scene.add.circle(0, 0, 22, 0xfff9c4, 0.4);

    this.iconContainer.add([glow, iconElement]);
    this.iconContainer.setAlpha(0);

    // Fade in
    this.scene.tweens.add({
      targets: this.iconContainer,
      alpha: 0.85,
      duration: 400,
      ease: 'Sine.easeOut',
    });

    // Gentle pulse for visibility
    this.scene.tweens.add({
      targets: this.iconContainer,
      scale: { from: 0.9, to: 1.05 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private updateIconVisibility(): void {
    if (!this.iconContainer) return;

    // Do NOT show if player is currently moving
    if (this.isPlayerMoving) {
      if (this.iconContainer.visible) {
        this.iconContainer.setVisible(false);
      }
      return;
    }

    // Show if we have a valid target and icon should be visible
    if (this.isVisible && this.targetPosition) {
      if (!this.iconContainer.visible) {
        this.iconContainer.setVisible(true);
      }
    }
  }

  private destroyIcon(): void {
    if (this.iconContainer) {
      this.scene.tweens.killTweensOf(this.iconContainer);
      this.iconContainer.destroy(true);
      this.iconContainer = null;
    }
  }
}
